import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, MessageCircle } from 'lucide-react';
import Modal from '../../../shared/components/ui/Modal';
import Button from '../../../shared/components/ui/Button';
import {
  bookSchedulingSlot,
  getSchedulingSlots,
  sendSchedulingBookingLink,
  startWhatsAppBooking
} from '../../services/api/implementation/impl';
import {
  schedulingLeadMeetingsQueryKey,
  schedulingSlotsQueryKey
} from '../../utils/schedulingAccess';
import toast from 'react-hot-toast';

/** Next N calendar dates, labelled in the viewer's locale. */
function buildDateOptions(count = 14) {
  const out = [];
  const today = new Date();
  for (let i = 0; i < count; i++) {
    const day = new Date(today);
    day.setDate(today.getDate() + i);
    const value = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(
      day.getDate()
    ).padStart(2, '0')}`;
    out.push({
      value,
      label:
        i === 0
          ? 'Today'
          : day.toLocaleDateString(undefined, {
              weekday: 'short',
              day: '2-digit',
              month: 'short'
            })
    });
  }
  return out;
}

function formatSlot(iso, timeZone) {
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      ...(timeZone ? { timeZone } : {})
    });
  } catch {
    return new Date(iso).toISOString().slice(11, 16);
  }
}

/**
 * Two shapes, one modal.
 *
 * A backend we can drive through its API (Easy!Appointments) shows real slots
 * and books one. A backend that owns its own booking page (Calendly) can only
 * be handed to the lead as a link. Which one is decided by capability flags
 * from the server, never by the provider's name.
 */
export default function BookMeetingModal({ leadId, lead, mode, onClose }) {
  const isLinkMode = mode === 'link';
  const queryClient = useQueryClient();
  const dateOptions = useMemo(() => buildDateOptions(), []);
  const [date, setDate] = useState(dateOptions[0].value);
  const [selectedStart, setSelectedStart] = useState(null);

  const {
    data,
    isFetching,
    isError,
    error
  } = useQuery({
    queryKey: schedulingSlotsQueryKey(leadId, date),
    queryFn: () => getSchedulingSlots(leadId, { date }),
    enabled: !isLinkMode,
    retry: false,
    // Availability goes stale quickly — someone else may take a slot.
    staleTime: 30 * 1000
  });

  const slots = data?.slots ?? [];
  const host = data?.host;
  const timeZone = data?.timezone;

  const bookMutation = useMutation({
    mutationFn: () => bookSchedulingSlot(leadId, { start: selectedStart }),
    onSuccess: () => {
      toast.success('Meeting booked');
      queryClient.invalidateQueries({
        queryKey: schedulingLeadMeetingsQueryKey(leadId)
      });
      onClose();
    },
    onError: (err) => {
      // 409 means the slot went while the modal was open, not a server fault.
      if (err?.response?.status === 409) {
        toast.error(
          err?.response?.data?.message ||
            'That slot was just taken. Pick another time.'
        );
        setSelectedStart(null);
        queryClient.invalidateQueries({
          queryKey: schedulingSlotsQueryKey(leadId, date)
        });
        return;
      }
      toast.error(err?.response?.data?.message || 'Could not book that slot');
    }
  });

  const whatsappMutation = useMutation({
    mutationFn: () => startWhatsAppBooking(leadId),
    onSuccess: () => {
      toast.success('Booking options sent on WhatsApp');
      onClose();
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message || 'Could not send options on WhatsApp'
      );
    }
  });

  const leadEmail = lead?.user?.email?.trim() || null;
  const leadPhone = lead?.user?.phone_number?.trim() || null;
  const [linkChannels, setLinkChannels] = useState(() => {
    const initial = [];
    if (leadPhone) initial.push('whatsapp');
    if (leadEmail) initial.push('email');
    return initial;
  });
  const [linkResults, setLinkResults] = useState(null);

  const linkMutation = useMutation({
    mutationFn: () =>
      sendSchedulingBookingLink(leadId, { channels: linkChannels }),
    onSuccess: (result) => {
      setLinkResults(result?.results ?? null);
      const failed = (result?.results ?? []).filter((r) => !r.sent);
      toast.success(
        failed.length ? 'Link sent — some channels failed' : 'Booking link sent'
      );
      queryClient.invalidateQueries({
        queryKey: schedulingLeadMeetingsQueryKey(leadId)
      });
    },
    onError: (err) => {
      // Total failure returns 400 with the same body shape as a partial send.
      setLinkResults(err?.response?.data?.data?.results ?? null);
      toast.error(err?.response?.data?.message || 'Could not send the link');
    }
  });

  const toggleChannel = (channel) =>
    setLinkChannels((prev) =>
      prev.includes(channel) ? prev.filter((c) => c !== channel) : [...prev, channel]
    );

  if (isLinkMode) {
    return (
      <Modal isDirty confirmOnClose name="Send booking link" onClose={onClose} width={44}>
        <div className="space-y-5 p-1">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-teal-50 p-2 text-[#1E4A53]">
              <CalendarClock className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Send {lead?.user?.name || 'this lead'} a booking link
              </h2>
              <p className="text-sm text-gray-600">
                This backend owns its own booking page, so the lead picks a time
                there. The booking syncs back onto this lead automatically.
              </p>
            </div>
          </div>

          <div>
            <label className="mb-2 block text-xs font-semibold uppercase text-gray-500">
              Send over
            </label>
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={linkChannels.includes('whatsapp')}
                  disabled={!leadPhone}
                  onChange={() => toggleChannel('whatsapp')}
                />
                WhatsApp
                {leadPhone ? (
                  <span className="text-xs text-gray-500">({leadPhone})</span>
                ) : (
                  <span className="text-xs text-amber-700">no phone number</span>
                )}
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={linkChannels.includes('email')}
                  disabled={!leadEmail}
                  onChange={() => toggleChannel('email')}
                />
                Email
                {leadEmail ? (
                  <span className="text-xs text-gray-500">({leadEmail})</span>
                ) : (
                  <span className="text-xs text-amber-700">no email</span>
                )}
              </label>
            </div>
          </div>

          {linkResults?.length ? (
            <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm">
              <p className="mb-2 font-medium text-gray-900">Result</p>
              <ul className="space-y-1">
                {linkResults.map((r) => (
                  <li key={r.channel} className="text-xs">
                    <span className="font-semibold capitalize">{r.channel}</span>:{' '}
                    {r.sent ? (
                      <span className="text-green-700">sent</span>
                    ) : (
                      <span className="text-red-700">{r.error || 'failed'}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="flex items-center justify-end gap-3 border-t border-gray-200 pt-4">
            <Button displayType="secondary" onClick={onClose}>
              {linkResults?.some((r) => r.sent) ? 'Done' : 'Cancel'}
            </Button>
            <Button
              displayType="primary"
              isLoading={linkMutation.isPending}
              disabled={!linkChannels.length || linkMutation.isPending}
              onClick={() => linkMutation.mutate()}>
              Send link
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isDirty confirmOnClose name="Book a meeting" onClose={onClose} width={48}>
      <div className="space-y-5 p-1">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-teal-50 p-2 text-[#1E4A53]">
            <CalendarClock className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Book a meeting with {lead?.user?.name || 'this lead'}
            </h2>
            <p className="text-sm text-gray-600">
              Pick a real open slot, or send the lead the same options on WhatsApp
              and let them choose.
            </p>
          </div>
        </div>

        {host ? (
          <div
            className={`rounded-xl border px-4 py-3 text-xs leading-relaxed ${
              host.onExecutiveCalendar
                ? 'border-gray-200 bg-gray-50 text-gray-700'
                : 'border-amber-300 bg-amber-50 text-amber-950'
            }`}>
            {host.onExecutiveCalendar ? (
              <>
                <span className="font-medium text-gray-900">
                  Books on {host.name || 'your'} calendar
                </span>{' '}
                — only times you are free are shown.
              </>
            ) : (
              <>
                <span className="font-medium">
                  Books on the builder&apos;s shared calendar.
                </span>{' '}
                You don&apos;t have your own calendar in the scheduling system, so
                this won&apos;t check your availability. Ask your admin to add you.
              </>
            )}
          </div>
        ) : null}

        <div>
          <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">
            Date
          </label>
          <select
            value={date}
            onChange={(e) => {
              setDate(e.target.value);
              setSelectedStart(null);
            }}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
            {dateOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-2 block text-xs font-semibold uppercase text-gray-500">
            Available times
            {data?.service?.name ? (
              <span className="ml-1 normal-case text-gray-400">
                · {data.service.name} ({data.service.durationMinutes} min)
              </span>
            ) : null}
          </label>

          {isFetching ? (
            <p className="text-sm text-gray-500">Loading times…</p>
          ) : isError ? (
            <p className="text-sm text-red-700">
              {error?.response?.data?.message || 'Could not load available times.'}
            </p>
          ) : !slots.length ? (
            <p className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-4 text-center text-sm text-gray-500">
              No free times on this day. Try another date.
            </p>
          ) : (
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
              {slots.map((slot) => {
                const active = selectedStart === slot.start;
                return (
                  <button
                    key={slot.start}
                    type="button"
                    onClick={() => setSelectedStart(slot.start)}
                    className={`rounded-lg border px-2 py-2 text-sm transition ${
                      active
                        ? 'border-[#1E4A53] bg-[#1E4A53] text-white'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-[#1E4A53]'
                    }`}>
                    {formatSlot(slot.start, timeZone)}
                  </button>
                );
              })}
            </div>
          )}
          {timeZone ? (
            <p className="mt-2 text-xs text-gray-500">Times shown in {timeZone}.</p>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-4">
          <Button
            displayType="secondary"
            isLoading={whatsappMutation.isPending}
            disabled={whatsappMutation.isPending || bookMutation.isPending}
            onClick={() => whatsappMutation.mutate()}>
            <span className="flex items-center gap-1">
              <MessageCircle className="h-4 w-4" />
              Let them pick on WhatsApp
            </span>
          </Button>

          <Button
            displayType="primary"
            isLoading={bookMutation.isPending}
            disabled={!selectedStart || bookMutation.isPending}
            onClick={() => bookMutation.mutate()}>
            Book selected time
          </Button>
        </div>
      </div>
    </Modal>
  );
}
